<template>
  <v-card
    :disabled="map.loading"
    class="d-flex justify-center flex-column dashboard-card"
    :to="`/${map.id}/edit`"
  >
    <v-img
      height="100"
      :src="tileImage"
      cover
    >
      <div class="loading-container">
        <v-progress-circular
          v-if="map.loading"
          :size="25"
          :width="5"
          indeterminate
          color="primary"
        />
      </div>
    </v-img>
    <v-card-title
      :data-testid="`map-card-title-${map.id}`"
      class="text-subtitle-2 text-left text-grey-darken-3 dashboard-card__title"
    >
      {{ map.name }}
    </v-card-title>
    <v-card-actions class="pa-0 mr-4 ml-4">
      <div class="flex-grow-1 text-left font-weight-regular mr-2">
        <p class="lastEditedByFont">
          {{ map.editedOn.name }}
          <br>
          {{ lastEditedDate }}
        </p>
      </div>
      <div class="d-flex justify-end align-center ga-2 dashboard-card__actions">
        <ShareDialog
          :map-id="map.id"
          :show-dialog="showShareDialog"
          :is-map-view="false"
          @close="showShareDialog = false"
        />
        <!-- Share Button -->
        <v-btn
          variant="text"
          size="x-small"
          data-testid="share-map-button"
          :icon="getShareIcon"
          @click.prevent="showShareDialog = true"
        />
        <!-- Delete button -->
        <v-btn
          v-if="userCanEditMap"
          variant="text"
          data-testid="delete-map-button"
          icon="mdi-delete"
          size="x-small"
          @click.prevent="$emit('delete')"
        />
        <!-- Options Button -->
        <v-menu location="bottom">
          <template #activator="{ props }">
            <v-btn
              variant="text"
              data-testid="options-button"
              size="x-small"
              icon="mdi-dots-vertical"
              v-bind="props"
              @click.prevent
            />
          </template>
          <v-list
            density="compact"
            class="py-0"
          >
            <v-list-item
              v-if="userCanEditMap"
              ref="duplicate-map-button"
              class="px-2"
              @click="$emit('duplicate')"
            >
              <v-list-item-title class="text-subtitle-1">
                Duplicate
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              class="px-2"
              @click="showShareHistory = true"
            >
              <v-list-item-title class="text-subtitle-1">
                Sharing History
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              class="px-2"
              @click="showUsageTrends = true"
            >
              <v-list-item-title class="text-subtitle-1">
                Usage Trend
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <ShareHistory
          v-if="showShareHistory"
          :map-id="map.id"
          @closed="showShareHistory = false"
        />
        <UsageTrends
          v-if="showUsageTrends"
          :map-id="map.id"
          :map-name="map.name"
          @closed="showUsageTrends = false"
        />
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import {currentUser} from '@/services/FirestoreService';
import NPEditorShareIcon from '@/components/svgs/MenuIcons/NPEditorShareIcon.vue';
import NPViewerShareIcon from '@/components/svgs/MenuIcons/NPViewerShareIcon.vue';
import ShareDialog from './ShareSettings/ShareDialog.vue';
import ShareHistory from './ShareSettings/ShareHistory.vue';
import UsageTrends from './UsageTrends/UsageTrends.vue';

export default {
  name: 'MapCard',
  components: {
    ShareDialog,
    ShareHistory,
    UsageTrends,
  },
  props: {
    map: {
      type: Object,
      required: true,
    },
  },
  emits: ['delete', 'duplicate'],
  data() {
    return {
      tileImage: 'https://storage.googleapis.com/beyond_maps_public/Icons_Browser/Shareable-Map-Tile.png',
      showShareDialog: false,
      showShareHistory: false,
      showUsageTrends: false,
    };
  },
  computed: {
    getShareIcon() {
      return this.userCanEditMap ? NPEditorShareIcon : NPViewerShareIcon;
    },
    lastEditedDate() {
      const timestamp = parseInt(this.map.editedOn.timestamp, 10);
      const date = new Date(timestamp);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    },
    userCanEditMap() {
      // TODO: Remove when all entries in Algolia have been updated to include the share field.
      if (!this.map.share) return false;
      const user = currentUser();
      // This shouldn't be possible, since the dashboard cannot be accessed anonymously, but just in case.
      if (!user) return false;
      // User is the one who created the map.
      if (this.map?.createdOn?.uid === user.uid) return true;
      // Map is currently editable by all NP employees.
      if (this.map?.share?.accessLevel === 'edit') return true;
      // Map is currently not editable by NP employees, but the user is in the allow list.
      if (this.map?.share?.accessLevel === 'view' && this.map.share.users.includes(user.uid)) return true;
      return false;
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-card {
  margin: 12px;
  width: 250px;
  height: 200px;

  @include main.custom-margin(1470, 12, 44);
  @include main.custom-margin(1510, 12, 22);
  @include main.custom-margin(1605, 12, 32);
  @include main.custom-margin(1728, 12, 44);
  @include main.custom-margin(1800, 12, 22);
  @include main.custom-margin(1915, 12, 32);

  :deep(.v-card-title) {
    white-space: normal;
    min-height: 20%;
    height: 20%;
  }
  &__title {
    line-height: 1.1rem;
    padding: 5px 15px 0px 15px;
  }
}
.lastEditedByFont {
  font-size: 9pt;
}
.dashboard-card__actions {
  :deep(.v-btn) {
    width: 18px;
    height: 18px;
  }
}
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
